
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetTransactionsV2, GetProfileDetails } from './apis/get';
import { PatchUpdateTransactionStatus } from './apis/patch';
import { 
  CreditCard, 
  Search, 
  ArrowUpRight, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  RefreshCw,
  TrendingUp,
  Wallet,
  ShieldCheck,
  User,
  Calendar,
  Download,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  Mail,
  MinusCircle,
  PlusCircle,
  Activity,
  Server,
  Terminal,
  Info,
  ExternalLink,
  Eye
} from 'lucide-react';

interface Transaction {
  transaction_id: number;
  merchant_transaction_id: string | null;
  payment_transaction_id: string | null;
  status: string;
  transaction_type: string;
  credits: number;
  amount_paid: string | null;
  gross_amount?: string | number | null;
  discount_amount?: string | number | null;
  discount_percentage?: number | null;
  coupon_code?: string | null;
  description: string;
  created_at: string;
  payment_details: {
    payment_state: string | null;
    payment_type: string | null;
    payment_utr: string | null;
    payment_payer_vpa: string | null;
    payment_ifsc: string | null;
    payment_upi_transaction_id: string | null;
    payment_account_type: string | null;
    payment_masked_account_number: string | null;
    account_holder_name: string | null;
    payment_url: string | null;
  };
  user_details: {
    user_id: string;
    username: string;
    email: string;
    phone: string | number;
    gender: string;
    community: string;
    credit_balance: number;
    is_active: boolean;
    platform: string | null;
    version: string | null;
    name: string;
    city: string;
    state: string;
    age: number;
  }
}

interface Summary {
  total_amount: number;
  total_credits_purchased: number;
  total_credits_spent: number;
  success_count: number;
  pending_count: number;
  failed_count: number;
}

interface Pagination {
  total_transactions: number;
  current_page_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  total_pages: number;
}

const StatusDonutChart = ({ success, pending, failed }: { success: number, pending: number, failed: number }) => {
  const total = success + pending + failed || 1;
  const successPerc = (success / total) * 100;
  const pendingPerc = (pending / total) * 100;
  const failedPerc = (failed / total) * 100;

  // SVG Circle properties
  const size = 100;
  const center = size / 2;
  const radius = 38;
  const circumference = 2 * Math.PI * radius;

  const successOffset = circumference - (successPerc / 100) * circumference;

  return (
    <div className="relative w-32 h-32 flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
        {/* Background Circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="10"
        />
        {/* Success Segment */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke="#10b981"
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={successOffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
        {/* Pending Segment - Stacked */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke="#f59e0b"
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (pendingPerc / 100) * circumference}
          strokeLinecap="round"
          style={{ transform: `rotate(${(successPerc / 100) * 360}deg)`, transformOrigin: 'center' }}
          className="transition-all duration-1000 ease-out"
        />
        {/* Failed Segment - Stacked */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke="#f43f5e"
          strokeWidth="10"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (failedPerc / 100) * circumference}
          strokeLinecap="round"
          style={{ transform: `rotate(${((successPerc + pendingPerc) / 100) * 360}deg)`, transformOrigin: 'center' }}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center transform rotate-0">
        <span className="text-xl font-black text-white">{Math.round((success / total) * 100)}%</span>
        <span className="text-[8px] font-bold text-emerald-500 uppercase tracking-tighter">Success</span>
      </div>
    </div>
  );
};

export default function Transactions() {
  const navigate = useNavigate();
  const [data, setData] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState<Transaction | null>(null);
  const [profileDetails, setProfileDetails] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  const [updateStatusTxn, setUpdateStatusTxn] = useState<Transaction | null>(null);
  const [newStatus, setNewStatus] = useState<string>("SUCCESS");
  const [statusDescription, setStatusDescription] = useState<string>("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  const handleUpdateStatusSubmit = async () => {
    if (!updateStatusTxn) return;
    setIsUpdatingStatus(true);
    try {
      const response = await PatchUpdateTransactionStatus(updateStatusTxn.transaction_id, {
        status: newStatus,
        description: statusDescription
      });
      if (response && (response.status === true || response.status === "success" || response.status === 200)) {
        setUpdateStatusTxn(null);
        setStatusDescription("");
        setNewStatus("SUCCESS");
        fetchTransactions();
      }
    } catch (error) {
      console.error("Failed to update status", error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const [filters, setFilters] = useState({
    status: '',
    transaction_type: '',
    user_id: '',
    from_date: '',
    to_date: '',
    limit: 10,
    offset: 0
  });

  const fetchTransactions = useCallback(async (currentFilters = filters) => {
    setLoading(true);
    try {
      const response = await GetTransactionsV2(currentFilters);
      if (response && response.status === 'success') {
        setData(response.data || []);
        setSummary(response.summary || null);
        setPagination(response.pagination || null);
      }
    } catch (error) {
      console.error("Failed to fetch transactions", error);
      // toast.error("Failed to sync transactions");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const fetchProfileDetails = async (userId: string) => {
    setLoadingProfile(true);
    try {
      const response = await GetProfileDetails(userId);
      if (response && (response.status === 'success' || response.status === true)) {
        setProfileDetails(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch profile details", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({ 
      ...prev, 
      [name]: value, 
      ...(name !== 'offset' ? { offset: 0 } : {}) 
    }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchTransactions();
  };

  const getStatusStyle = (status: string) => {
    switch (status.toUpperCase()) {
      case 'SUCCESS': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'FAILED': return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
      case 'PENDING': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'SUCCESS': return <CheckCircle2 size={14} />;
      case 'FAILED': return <XCircle size={14} />;
      case 'PENDING': return <Clock size={14} />;
      default: return null;
    }
  };

  const getTypeStyle = (type: string) => {
    const t = type.toUpperCase();
    if (t.includes('PURCHASE')) return 'text-blue-400';
    if (t.includes('SPEND') || t.includes('UNLOCK')) return 'text-amber-400';
    if (t.includes('GRANT')) return 'text-emerald-400';
    return 'text-slate-400';
  };

  return (
    <div className="min-h-screen bg-transparent text-slate-200 p-8 space-y-8 relative font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
        <div>
          <h1 className="text-3xl font-extrabold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
            Financial Ledger
          </h1>
          <p className="text-slate-500 font-medium tracking-tight">Enterprise Transaction Analytics & User Ledger</p>
        </div>
        <div className="flex gap-3">
            <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-900/50 hover:bg-slate-800 text-slate-300 font-bold rounded-2xl border border-slate-800 transition active:scale-95 shadow-inner group">
                <Download size={18} className="group-hover:text-indigo-400 transition-colors" />
                Export CSV
            </button>
            <button 
                onClick={() => fetchTransactions()}
                className="flex items-center gap-2 px-7 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 transition active:scale-95 group"
            >
                <RefreshCw size={18} className={`${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                Refresh
            </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800/60 p-6 rounded-[2.5rem] shadow-xl hover:border-indigo-500/30 transition-all group overflow-hidden relative">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-indigo-500/5 rounded-full blur-2xl group-hover:bg-indigo-500/10 transition-all"></div>
            <div className="flex items-center justify-between mb-6 text-slate-400">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] group-hover:text-indigo-400 transition-colors">Total Revenue</span>
              <div className="bg-indigo-500/10 p-2.5 rounded-2xl text-indigo-400 group-hover:scale-110 transition-transform shadow-inner">
                <TrendingUp size={24} />
              </div>
            </div>
            <div className="text-4xl font-black text-white tracking-tighter">₹{summary.total_amount.toLocaleString()}</div>
            <div className="flex items-center gap-2 mt-4 text-xs">
               <div className="flex items-center gap-1 text-emerald-400 font-bold bg-emerald-500/10 px-2 py-1 rounded-lg">
                <ArrowUpRight size={14} />
                <span>Analytics Ready</span>
               </div>
            </div>
          </div>

          <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800/60 p-6 rounded-[2.5rem] shadow-xl hover:border-emerald-500/30 transition-all group overflow-hidden relative">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl group-hover:bg-emerald-500/10 transition-all"></div>
            <div className="flex items-center justify-between mb-6 text-slate-400">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] group-hover:text-emerald-400 transition-colors">Credit Flow</span>
              <div className="bg-emerald-500/10 p-2.5 rounded-2xl text-emerald-400 group-hover:scale-110 transition-transform shadow-inner">
                <Wallet size={24} />
              </div>
            </div>
            <div className="text-4xl font-black text-white tracking-tighter">{summary.total_credits_purchased.toLocaleString()}</div>
            <div className="flex items-center gap-2 mt-4 text-xs font-bold text-emerald-500 uppercase tracking-widest">
                Purchased Credits
            </div>
          </div>

          <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800/60 p-6 rounded-[2.5rem] shadow-xl hover:border-blue-500/30 transition-all group overflow-hidden relative">
            <div className="absolute -right-4 -top-4 w-24 h-24 bg-blue-500/5 rounded-full blur-2xl group-hover:bg-blue-500/10 transition-all"></div>
            <div className="flex items-center justify-between mb-1 text-slate-400">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] group-hover:text-blue-400 transition-colors">Status Distribution</span>
              <div className="bg-blue-500/10 p-2.5 rounded-2xl text-blue-400 group-hover:scale-110 transition-transform shadow-inner">
                <Activity size={24} />
              </div>
            </div>
            
            <div className="flex items-center gap-6">
                <StatusDonutChart success={summary.success_count} pending={summary.pending_count} failed={summary.failed_count} />
                <div className="flex flex-col gap-3 flex-1">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Success</span>
                        </div>
                        <span className="text-sm font-black text-white">{summary.success_count}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]"></div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Pending</span>
                        </div>
                        <span className="text-sm font-black text-white">{summary.pending_count}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]"></div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Failed</span>
                        </div>
                        <span className="text-sm font-black text-white">{summary.failed_count}</span>
                    </div>
                </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-6 shadow-2xl space-y-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <input
                    type="text"
                    placeholder="Search User ID..."
                    value={filters.user_id}
                    onChange={(e) => handleFilterChange('user_id', e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 transition-all font-medium shadow-inner"
                />
            </div>
            
            <select 
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 appearance-none font-medium cursor-pointer min-w-[140px] shadow-inner"
            >
                <option value="">All Statuses</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
                <option value="PENDING">Pending</option>
            </select>



            <div className="flex gap-2">
                <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input 
                        type="date" 
                        value={filters.from_date}
                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                        className="pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm shadow-inner"
                    />
                </div>
                <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" size={16} />
                    <input 
                        type="date" 
                        value={filters.to_date}
                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                        className="pl-10 pr-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm shadow-inner"
                    />
                </div>
            </div>

            <button 
                type="submit"
                className="px-8 py-3 bg-indigo-600 border border-indigo-500 text-white font-bold rounded-2xl hover:bg-indigo-500 transition active:scale-95 shadow-lg shadow-indigo-500/20"
            >
                Sync Records
            </button>
        </form>
      </div>

      <div className="bg-slate-900/30 backdrop-blur-md border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-900/80 border-b border-slate-800/50">
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Transaction Ref</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Beneficiary</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 text-center">Protocol</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 text-center">Coupon</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 text-center">Credits</th>                 <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 text-center">State</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Timeline</th>
                <th className="px-6 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/10">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-24 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                      <p className="text-slate-500 font-black tracking-[0.15em] uppercase text-[10px]">Synchronizing Financial Records...</p>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-24 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-3">
                        <Activity size={48} className="opacity-10 text-indigo-500" />
                        <p className="font-bold text-lg text-slate-400">Zero Ledger Entries Matching Filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((txn) => (
                  <tr 
                    key={txn.transaction_id} 
                    onClick={() => setSelectedTxn(txn)}
                    className="group hover:bg-white/[0.04] transition-all border-b border-slate-800/20 cursor-pointer active:bg-white/[0.06]"
                  >
                    <td className="px-6 py-6">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] font-black text-white bg-indigo-500/10 px-1.5 py-0.5 rounded border border-indigo-500/20">{txn.transaction_id}</span>
                            </div>
                            <div className="flex items-center gap-1.5 opacity-40 group-hover:opacity-100 transition-opacity">
                                <Terminal size={10} className="text-slate-400" />
                                <span className="text-[10px] font-mono text-slate-500 truncate max-w-[120px]" title={txn.merchant_transaction_id || 'LOCAL'}>
                                    {txn.merchant_transaction_id || 'LOCAL_SYNC'}
                                </span>
                            </div>
                            <div className="text-[9px] text-slate-500 mt-1 font-bold uppercase tracking-tighter truncate max-w-[150px]" title={txn.description}>
                                {txn.description}
                            </div>
                        </div>
                    </td>
                    <td className="px-6 py-6">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center text-indigo-400 group-hover:border-indigo-500/40 transition-all shadow-inner relative overflow-hidden shrink-0">
                            <User size={18} />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-100 group-hover:text-indigo-400 transition-colors uppercase tracking-tight text-xs truncate">
                              {(txn.user_details?.name || txn.user_details?.username || "Unknown Entity")}
                            </span>
                            {txn.user_details?.user_id && (
                               <button
                                 onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/users/user/${txn.user_details.user_id}`, { state: { from: 'transactions' } });
                                  }}
                                 className="p-1 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded border border-blue-500/20 transition-all flex items-center gap-1 group/btn"
                                 title="View Full Profile"
                                >
                                 <Eye size={10} className="group-hover/btn:scale-110 transition-transform" />
                                 <span className="text-[8px] font-black uppercase tracking-tighter">View</span>
                               </button>
                             )}
                          </div>
                          <span className="text-[9px] text-slate-500 font-mono tracking-wider">
                            {txn.user_details?.user_id || "U-UNKNOWN"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6 text-center">
                        <span className={`px-2 py-1 rounded-lg text-[8px] font-black tracking-widest uppercase bg-slate-950/80 border border-slate-800/50 ${getTypeStyle(txn.transaction_type)}`}>
                            {txn.transaction_type.replace(/_/g, ' ')}
                        </span>
                    </td>
                    <td className="px-6 py-6 text-center">
                      {txn.coupon_code ? (
                        <div className="flex flex-col items-center gap-1">
                          <span className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase border border-amber-500/20 bg-amber-500/10 text-amber-400">
                            {txn.coupon_code}
                          </span>
                          {txn.discount_percentage ? (
                            <span className="text-[9px] font-bold text-emerald-400">
                              {txn.discount_percentage}% off
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-600 uppercase">
                          No coupon
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-6 text-center">
                        <div className={`text-base font-black tracking-tight flex items-center justify-center gap-1 ${txn.credits >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {txn.credits > 0 ? <PlusCircle size={14}/> : (txn.credits < 0 ? <MinusCircle size={14}/> : null)}
                          {Math.abs(txn.credits)}
                        </div>
                    </td>
                    <td className="px-6 py-6 text-center">
                      <div className="flex flex-col items-center gap-1.5 text-[9px]">
                        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-black uppercase border group-hover:shadow-lg transition-all ${getStatusStyle(txn.status)}`}>
                            {getStatusIcon(txn.status)}
                            {txn.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-6">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-300 tracking-tight">
                                {new Date(txn.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                            </span>
                            <span className="text-[9px] text-slate-600 font-bold uppercase tracking-tighter">
                                {new Date(txn.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </td>
                    <td className="px-6 py-6 text-center">
                      {(txn.status?.toUpperCase() === 'FAILED' || txn.status?.toUpperCase() === 'PENDING') ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUpdateStatusTxn(txn);
                            setNewStatus(txn.status.toUpperCase());
                          }}
                          className="px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 hover:bg-indigo-600 hover:text-white rounded-xl transition-all text-[10px] font-black uppercase tracking-wider active:scale-95"
                        >
                          Update Status
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-600 font-bold uppercase">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {pagination && (
            <div className="bg-slate-900/80 px-8 py-5 flex items-center justify-between border-t border-slate-800/50">
                <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
                    Entries <span className="text-indigo-400">{pagination.offset + 1}</span> — <span className="text-indigo-400">{Math.min(pagination.offset + pagination.limit, pagination.total_transactions)}</span> of <span className="text-white">{pagination.total_transactions}</span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-[10px] font-bold text-slate-600 uppercase">Page {Math.floor(pagination.offset / pagination.limit) + 1} / {pagination.total_pages}</span>
                    <div className="flex gap-2">
                        <button 
                            disabled={pagination.offset === 0}
                            onClick={(e) => { e.stopPropagation(); handleFilterChange('offset', Math.max(0, pagination.offset - pagination.limit)); }}
                            className="p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 shadow-inner"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button 
                            disabled={!pagination.has_more}
                            onClick={(e) => { e.stopPropagation(); handleFilterChange('offset', pagination.offset + pagination.limit); }}
                            className="p-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 shadow-inner"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>
        )}
      </div>

      {selectedTxn && (
        <div className="fixed inset-0 z-50 flex items-center justify-end p-4">
            <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={() => setSelectedTxn(null)}></div>
            <div className="relative bg-slate-900 border-l border-slate-800 h-full w-full max-w-2xl shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300 rounded-l-[3rem]">
                <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-md px-10 py-8 border-b border-slate-800 flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h2 className="text-2xl font-black text-white uppercase tracking-tighter italic">Ledger Entry Detail</h2>
                            <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase border ${getStatusStyle(selectedTxn?.status || "")}`}>
                                {selectedTxn?.status}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-indigo-400 font-mono text-[11px] font-bold tracking-widest uppercase">
                            <Terminal size={12} />
                            {selectedTxn?.transaction_id}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {selectedTxn?.user_details?.user_id && (
                            <button
                                onClick={() => selectedTxn?.user_details?.user_id && fetchProfileDetails(selectedTxn.user_details.user_id)}
                                disabled={loadingProfile}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black uppercase rounded-xl transition-all shadow-lg shadow-blue-500/20 active:scale-95 disabled:opacity-50"
                            >
                                {loadingProfile ? (
                                    <RefreshCw size={14} className="animate-spin" />
                                ) : (
                                    <Eye size={14} />
                                )}
                                Sync Profile
                            </button>
                        )}
                        <button onClick={() => { setSelectedTxn(null); setProfileDetails(null); }} className="p-3 bg-slate-950/50 hover:bg-slate-800 rounded-2xl transition-all text-slate-400 hover:text-rose-500 shadow-inner group">
                            <XCircle size={28} className="group-hover:rotate-90 transition-transform" />
                        </button>
                    </div>
                </div>

                <div className="p-10 space-y-12 pb-24">
                    {profileDetails && (
                        <div className="bg-blue-500/5 border border-blue-500/20 rounded-[2.5rem] p-8 animate-in fade-in slide-in-from-top-4 duration-500">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-[11px] font-black text-blue-400 uppercase tracking-[0.3em] flex items-center gap-3">
                                    <div className="p-2 bg-blue-500/10 rounded-xl"><Info size={16} /></div>
                                    Live Profile Synchronization
                                </h3>
                                <button 
                                    onClick={() => {
                                        const userId = selectedTxn?.user_details?.user_id;
                                        if (userId) navigate(`/users/user/${userId}`, { state: { from: 'transactions' } });
                                    }}
                                    className="flex items-center gap-2 text-[10px] font-black text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors"
                                >
                                    Full Profile <ExternalLink size={12} />
                                </button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                <div className="space-y-1">
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Marital Status</span>
                                    <span className="text-xs text-slate-200 font-black italic">{profileDetails.personal_details?.marital_status || 'N/A'}</span>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Highest Education</span>
                                    <span className="text-xs text-slate-200 font-black italic">{profileDetails.educational_details?.highest_education || 'N/A'}</span>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Occupation</span>
                                    <span className="text-xs text-slate-200 font-black italic">{profileDetails.professional_details?.occupation || 'N/A'}</span>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Annual Income</span>
                                    <span className="text-xs text-emerald-400 font-black italic">{profileDetails.professional_details?.annual_income || 'N/A'}</span>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Religion / Caste</span>
                                    <span className="text-xs text-slate-200 font-black italic">{profileDetails.personal_details?.religion || 'N/A'} / {profileDetails.personal_details?.caste || 'N/A'}</span>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest block">Onboarding</span>
                                    <span className={`text-[10px] font-black px-2 py-0.5 rounded border ${profileDetails.personal_details?.onboarding_complete ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                                        {profileDetails.personal_details?.onboarding_complete ? 'COMPLETE' : 'INCOMPLETE'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-6">
                        <div className="p-8 bg-slate-950/40 border border-slate-800 rounded-[2.5rem] shadow-inner relative group overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <TrendingUp size={48} />
                            </div>
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] block mb-2">Settlement Value</span>
                        <div className="text-3xl font-black text-white">{selectedTxn?.amount_paid ? `₹${parseFloat(selectedTxn.amount_paid).toLocaleString()}` : '₹0'}</div>
                        {selectedTxn?.coupon_code ? (
                          <div className="mt-2 space-y-1">
                            <div className="inline-flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-[9px] font-black uppercase text-amber-400">
                              {selectedTxn.coupon_code}
                              {selectedTxn.discount_percentage ? ` • ${selectedTxn.discount_percentage}% off` : ""}
                            </div>
                            <div className="text-[10px] text-slate-500 font-bold">
                              Gross {selectedTxn.gross_amount ? `₹${Number(selectedTxn.gross_amount).toLocaleString()}` : '₹0'} • Saved {selectedTxn.discount_amount ? `₹${Number(selectedTxn.discount_amount).toLocaleString()}` : '₹0'}
                            </div>
                          </div>
                        ) : null}
                        <div className="mt-3 flex items-center gap-1.5 text-slate-600 font-bold text-[9px] uppercase italic">
                            <ShieldCheck size={12} />
                            Verified Liquidity Inflow
                            </div>
                        </div>
                        <div className="p-8 bg-slate-950/40 border border-slate-800 rounded-[2.5rem] shadow-inner relative group overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <PlusCircle size={48} />
                            </div>
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] block mb-2">Resource Impact</span>
                            <div className={`text-3xl font-black flex items-center gap-2 ${selectedTxn && selectedTxn.credits >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {selectedTxn && selectedTxn.credits > 0 ? '+' : ''}{selectedTxn?.credits}
                                <span className="text-sm text-slate-500 font-bold uppercase tracking-tighter mt-2">Credits</span>
                            </div>
                            <div className="mt-3 flex items-center gap-1.5 text-slate-600 font-bold text-[9px] uppercase italic">
                                <Activity size={12} />
                                Balance Adjusted Automatically
                            </div>
                        </div>
                    </div>

                    <div className="space-y-10">
                        <div className="border border-slate-800/50 p-8 rounded-[2.5rem] bg-slate-950/20">
                            <h4 className="text-[11px] font-black text-indigo-400 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
                                <div className="p-2 bg-indigo-500/10 rounded-xl"><User size={16} /></div>
                                Identity & Account Profiling
                            </h4>
                            <div className="flex items-start gap-8">
                                <div className="w-20 h-20 rounded-3xl bg-slate-950 border border-slate-800 flex items-center justify-center text-indigo-400 shadow-inner group relative overflow-hidden shrink-0">
                                    <User size={32} />
                                    {selectedTxn?.user_details?.platform && (
                                        <div className="absolute bottom-1 right-1 p-1 bg-indigo-600 rounded-lg text-white">
                                            <Smartphone size={10} />
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-6 flex-1">
                                    <div className="flex flex-col">
                                        <span className="text-white font-black text-2xl uppercase tracking-tighter leading-tight flex items-center gap-3">
                                            {selectedTxn?.user_details?.name}
                                            <span className={`w-2.5 h-2.5 rounded-full ${selectedTxn?.user_details?.is_active ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500'}`} title={selectedTxn?.user_details?.is_active ? 'Active User' : 'Inactive User'}></span>
                                        </span>
                                        <span className="text-xs text-slate-500 font-bold italic opacity-80 mt-1">{selectedTxn?.user_details?.city || 'Undisclosed Location'}, {selectedTxn?.user_details?.state || 'Global'}</span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[9px] text-slate-600 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                <Mail size={10} /> Communication
                                            </span>
                                            <span className="text-xs text-slate-300 font-mono italic break-all leading-relaxed truncate" title={selectedTxn?.user_details?.email}>{selectedTxn?.user_details?.email}</span>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[9px] text-slate-600 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                <Smartphone size={10} /> Phone Entity
                                            </span>
                                            <span className="text-xs text-slate-300 font-mono font-black italic">{selectedTxn?.user_details?.phone}</span>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3 pt-2">
                                        <span className="px-3 py-1.5 rounded-xl bg-indigo-500/10 text-indigo-400 text-[10px] font-black uppercase border border-indigo-500/20 shadow-sm">{selectedTxn?.user_details?.gender} Profile</span>
                                        <span className="px-3 py-1.5 rounded-xl bg-slate-800 text-slate-400 text-[10px] font-black uppercase border border-white/5">{selectedTxn?.user_details?.age} YRS Old</span>
                                        <span className="px-3 py-1.5 rounded-xl bg-slate-800 text-slate-400 text-[10px] font-black uppercase border border-white/5 tracking-tighter">{selectedTxn?.user_details?.community}</span>
                                        {selectedTxn?.user_details?.platform && (
                                            <span className="px-3 py-1.5 rounded-xl bg-slate-800 text-slate-400 text-[10px] font-black uppercase border border-white/5 flex items-center gap-1.5 shadow-sm">
                                                <Smartphone size={12} /> {selectedTxn.user_details.platform} v{selectedTxn.user_details.version || '0.0'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border border-slate-800/50 p-8 rounded-[2.5rem] bg-slate-950/20">
                            <h4 className="text-[11px] font-black text-indigo-400 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
                                <div className="p-2 bg-indigo-500/10 rounded-xl"><Server size={16} /></div>
                                Synchronization & Protocol
                            </h4>
                            <div className="grid grid-cols-2 gap-10">
                                <div className="flex flex-col gap-2">
                                    <span className="text-[9px] text-slate-600 font-black uppercase tracking-widest flex items-center gap-1.5">
                                        <CreditCard size={10} /> Settlement Protocol
                                    </span>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-slate-200 font-bold uppercase tracking-tight">{selectedTxn?.payment_details?.payment_type || 'SYSTEM_INTERNAL'}</span>
                                        {selectedTxn?.payment_details?.payment_account_type && (
                                            <span className="text-[10px] text-slate-500 font-medium italic mt-0.5">{selectedTxn.payment_details.payment_account_type} Account</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="text-[9px] text-slate-600 font-black uppercase tracking-widest flex items-center gap-1.5">
                                        <Info size={10} /> External TXN Integrity
                                    </span>
                                    <span className="text-xs text-slate-400 font-mono tracking-widest break-all group relative">
                                        {selectedTxn?.merchant_transaction_id || selectedTxn?.payment_transaction_id || 'LOCAL_LEDGER_SYNC'}
                                        <div className="text-[8px] text-slate-600 mt-1 uppercase font-bold italic tracking-tighter">Verified Merchant Handshake</div>
                                    </span>
                                </div>
                                <div className="col-span-2 flex flex-col gap-2 border-t border-white/5 pt-6">
                                    {selectedTxn?.coupon_code && (
                                      <div className="mb-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
                                        <div className="text-[9px] text-slate-600 font-black uppercase tracking-widest mb-2">
                                          Coupon Attribution
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3">
                                          <span className="px-3 py-1 rounded-xl bg-amber-500/10 text-amber-400 text-[10px] font-black uppercase border border-amber-500/20">
                                            {selectedTxn.coupon_code}
                                          </span>
                                          <span className="text-[10px] font-bold text-slate-400">
                                            Discount: {selectedTxn.discount_percentage || 0}%
                                          </span>
                                          <span className="text-[10px] font-bold text-slate-400">
                                            Saved: {selectedTxn.discount_amount ? `₹${Number(selectedTxn.discount_amount).toLocaleString()}` : '₹0'}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    <span className="text-[9px] text-slate-600 font-black uppercase tracking-widest flex items-center gap-1.5">
                                        <Activity size={10} /> Description Log
                                    </span>
                                    <div className="p-5 bg-slate-950 rounded-2xl border border-slate-800/50 italic text-[11px] text-slate-400 leading-relaxed shadow-inner">
                                        "{selectedTxn?.description || 'No descriptive logs maintained for this transaction state.'}"
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-8 left-10 right-10 grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-center gap-3 text-[9px] text-slate-600 font-black uppercase tracking-[0.4em] py-4 border-t border-white/5">
                        <ShieldCheck size={14} className="text-emerald-500/50" />
                        Authenticated Financial Record
                    </div>
                </div>
            </div>
        </div>
      )}

      {updateStatusTxn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-md" onClick={() => setUpdateStatusTxn(null)}></div>
          <div className="relative bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Update Status</h3>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Transaction #{updateStatusTxn.transaction_id}</p>
              </div>
              <button 
                onClick={() => setUpdateStatusTxn(null)} 
                className="p-1.5 bg-slate-950/50 hover:bg-slate-850 rounded-xl text-slate-400 hover:text-rose-500 transition"
              >
                <XCircle size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Select New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 transition-all font-medium appearance-none cursor-pointer shadow-inner text-xs"
                >
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="FAILED">FAILED</option>
                  <option value="PENDING">PENDING</option>
                  <option value="REFUNDED">REFUNDED</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Description / Reason</label>
                <textarea
                  value={statusDescription}
                  onChange={(e) => setStatusDescription(e.target.value)}
                  placeholder="Payment was verified manually via bank statement"
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 transition-all font-medium shadow-inner resize-none text-xs leading-relaxed"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setUpdateStatusTxn(null)}
                className="flex-1 px-4 py-3 bg-slate-950 hover:bg-slate-850 text-slate-400 font-bold rounded-2xl border border-slate-800/80 transition active:scale-95 text-xs uppercase tracking-wider"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatusSubmit}
                disabled={isUpdatingStatus}
                className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-black rounded-2xl shadow-xl shadow-indigo-500/20 transition active:scale-95 text-xs uppercase tracking-wider flex items-center justify-center gap-2"
              >
                {isUpdatingStatus ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : null}
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-2 text-[10px] text-slate-600 font-black uppercase tracking-[0.4em] py-12">
          <ShieldCheck size={14} className="text-indigo-500/30" />
          Secure Financial Infrastructure Verified
      </div>
    </div>
  );
}
